import { useId, useState } from 'react';
import { PencilIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { taskFormSchema } from '../taskForm';
import { useRenameTask } from '../hooks/useTaskText';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * A task's title, editable in place in the drawer header.
 *
 * `WorkflowName`'s contract, deliberately: Enter or moving focus away commits,
 * `Esc` is the one way to discard, and a title the server refused keeps the
 * field open with the message beside it. Renaming a task and renaming a
 * workflow are the same gesture on two nouns, and two different sets of keys
 * for it would be a difference with nothing behind it.
 *
 * `Esc` stops propagating. The drawer around this is a dialog that closes on
 * `Esc`, and one key press should do one thing — discarding a draft *and*
 * closing the surface it was in is two.
 *
 * The input carries no `maxLength`. The attribute silently swallows the tail of
 * a paste, which is precisely the moment someone exceeds 200 characters and
 * precisely when they need to be told why rather than shown a truncation they
 * did not ask for.
 *
 * The open field states its own keys. The description beside it commits on an
 * explicit Save and shows a Save button saying so; this one commits on Enter or
 * on leaving the field, and showed nothing — so the panel's two text fields
 * ended editing by two different rules and only one of them ever said which.
 * The difference is worth keeping and worth stating; a reader should not have
 * to discover it by losing something.
 */
export function TaskTitleField({
  workspaceId,
  taskId,
  title,
}: {
  workspaceId: string;
  taskId: string;
  title: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [localError, setLocalError] = useState<string | null>(null);
  const rename = useRenameTask();
  const canWork = useCanWork();
  const errorId = useId();
  const fieldId = useId();

  const stopEditing = () => {
    setIsEditing(false);
    setLocalError(null);
    rename.reset();
  };

  /** The form schema's own rule, so the create dialog and this cannot drift. */
  const validate = (candidate: string): string | null => {
    const parsed = taskFormSchema.shape.title.safeParse(candidate);
    return parsed.success
      ? null
      : (parsed.error.issues[0]?.message ?? 'Invalid title.');
  };

  const commit = () => {
    if (rename.isPending) return;
    const trimmed = draft.trim();
    if (trimmed === title) {
      stopEditing();
      return;
    }
    const invalid = validate(trimmed);
    if (invalid !== null) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    rename.mutate(
      { workspaceId, taskId, title: trimmed },
      { onSuccess: stopEditing },
    );
  };

  if (!isEditing) {
    return (
      <div className="flex min-w-0 items-start gap-1">
        <h2 className="text-foreground min-w-0 text-base leading-snug font-semibold">
          {title}
        </h2>
        {canWork ? (
          <Button
            variant="ghost"
            size="icon-sm"
            // On touch the 44px target is centred on the title's first line
            // (spec 270), not hung 11px below it.
            className="shrink-0 pointer-coarse:-my-2.75"
            aria-label={`Rename ${title}`}
            onClick={() => {
              setDraft(title);
              setIsEditing(true);
            }}
          >
            <PencilIcon />
          </Button>
        ) : null}
      </div>
    );
  }

  const error =
    localError ?? (rename.error !== null ? rename.error.message : null);

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={fieldId}>Title</Label>
      <Input
        id={fieldId}
        autoFocus
        aria-invalid={error !== null}
        aria-describedby={error !== null ? errorId : undefined}
        value={draft}
        disabled={rename.isPending}
        onChange={(event) => {
          setDraft(event.target.value);
          setLocalError(null);
          // The refusal described the previous attempt. Kept, it would outlive
          // its subject — and because blur is suppressed while an error stands,
          // it would trap the field open.
          rename.reset();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            stopEditing();
          }
        }}
        onBlur={() => {
          if (rename.isPending) return;
          // A title the field has already refused stays open to be fixed;
          // anything else commits, so leaving the field never loses typing.
          const invalid = validate(draft.trim());
          if (invalid !== null) {
            setLocalError(invalid);
            return;
          }
          commit();
        }}
      />
      {error !== null ? (
        <span id={errorId} role="alert" className="text-destructive text-xs">
          {error}
        </span>
      ) : (
        <span className="text-muted-foreground text-xs">
          Enter to save · Esc to discard
        </span>
      )}
    </div>
  );
}
